import { Children, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import ChevronLeft from '@icon/ChevronLeft';
import ChevronRight from '@icon/ChevronRight';
import IconButton from '@base/IconButton/IconButton';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Grow from '@base/Grow/Grow';
import useWindowSize from '@hooks/useWindowSize';
import { mediaQuery, Spacings } from '@styles/index';
import Button from '@base/Button/Button';
import H3 from '@base/H3/H3';
import pushRoute from '@utils/pushRoute';

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const GreenH4 = styled(H4)`
  font-weight: bold;
  font-size: 20px;
  line-height: 150%;
  color: var(--color-i-green);
  @media (max-width: 70.95em) {
    font-size: 16px;
  }
`;
const StyH3 = styled(H3)`
  font-size: 32px;
  @media (max-width: 70.95em) {
    font-size: 22px;
  }
  @media (max-width: 47.95em) {
    padding: 0 16px;
  }
`;

const SubLabel = styled(P1)`
  font-size: 16px;
  margin-top: ${Spacings.SPACING_3B};
  @media (max-width: 70.95em) {
    margin-top: 8px;
  }
  @media (max-width: 47.95em) {
    padding: 0 16px;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  margin-left: -16px;
  margin-bottom: -32px;
  @media (max-width: 47.95em) {
    margin: 0;
    overflow: hidden;
  }
`;

const CardContainer = styled.div`
  padding-bottom: 32px;
  padding-left: 16px;
  display: flex;
  & > a,
  & > div {
    transform: ${({ pos, addPadding }) =>
      (pos &&
        `translate3d(calc(${-100 * pos}% - ${
          pos * addPadding ? 24 * pos : 0
        }px), 0, 0)`) ||
      'initial'};
    transition: transform 400ms ease-in, opacity 400ms ease-in;
  }
  @media (min-width: 70.95em) {
    overflow: hidden;
  }
  @media (max-width: 47.95em) {
    padding: 0;
    padding-bottom: 2.5rem;
    overflow: scroll;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    & > div:first-child {
      margin-left: 16px;
    }
  }
`;

const ControlButton = styled(IconButton)`
  position: absolute;
  /* turning off for now as suggested by design */
  /* visibility: hidden; */
  /* opacity: 0; */
  transition: visibility 0s linear 300ms, opacity 300ms;
  ${({ height }) =>
    height ? `top: calc((${height}/2) - 16px);` : 'top: calc(50% - 32px);'}
  box-shadow: ${boxShadowGenerator()};
  /* turning off for now as suggested by design */
  /* ${CarouselWrapper}:hover & {
    visibility: visible;
    opacity: 1;
    transition: visibility 0s linear 0s, opacity 300ms;
  } */
  ${({ direction }) => (direction === 'prev' ? 'left: 0px;' : 'right: -16px;')}
  z-index:300;
  @media (max-width: 70.95em) {
    opacity: 1;
    visibility: visible;
    ${({ direction }) => (direction === 'prev' ? 'left: 8px;' : 'right: -8px;')}
  }
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const CaraouselInfo = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_10B};
  align-items: center;
  padding: 0;
  grid-column: span 18;
  @media (max-width: 70.95em) {
    margin-bottom: 24px;
  }
`;

const Container = styled.div`
  width: 1136px;
  margin: auto;
  @media (max-width: 70.95em) {
    width: 728px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
  }
`;

const Right = styled.div`
  position: absolute;
  width: 120px;
  right: 0px;
  height: 100%;
  top: 0;
  z-index: 2;
  background: linear-gradient(
    270deg,
    var(--color-base-0) 0%,
    rgba(252, 252, 252, 0.5) 51.04%,
    rgba(252, 252, 252, 0) 100%
  );
  ${({ $hideBackground }) => ($hideBackground ? 'background: none;' : null)}
  @media (max-width: 47.95em) {
    display: none;
  }
`;
const ItemWrap = styled.div`
  opacity: ${({ opacity }) => opacity};
  display: flex;
  margin-right: ${({ $type }) =>
    $type === 'educators' ? `${Spacings.SPACING_6B}` : 0};
  & .carousel-wrapper-free-platform {
    width: 480px;
    ${mediaQuery.uptoMobile} {
      width: 320px;
    }
  }
`;

const SeeAll = styled(Button)`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    min-height: 40px;
    height: 40px;
    width: calc(100% - 32px);
    background: var(--color-i-white);
    border: 1px solid var(--color-divider);
    border-radius: 8px;
    color: var(--color-i-green);
    font-size: 14px;
    margin: 16px 16px 0;
  }
`;

const CardCarousel = ({
  children,
  label,
  subLabel,
  showControls,
  className,
  step,
  addPadding,
  as,
  href,
  height,
  rightShadowDiv,
  rightShadowDivHeight,
  dataAnalytics,
  type,
  handleSeeAll,
  slideAutomatically = false,
  extraBenefits,
  hideBackground
}) => {
  const { isMobile } = useWindowSize();
  const items = Children.toArray(children);
  const slideInterval = useRef();
  const [pos, setPos] = useState(0);

  const handleNext = () => {
    if (items.length - (pos + step) < step) {
      setPos(items.length - step);
    } else setPos(pos + step);

    if (pos === items.length - 1) setPos(0);
  };

  const handlePrev = () => {
    if (pos < step) {
      setPos(0);
    }

    if (pos > 0 && pos >= step) setPos(pos - step);
  };

  const autoSlide = () => {
    if (slideInterval.current) clearTimeout(slideInterval.current);
    slideInterval.current = setTimeout(handleNext, 6000);
  };

  useEffect(() => {
    if (slideAutomatically) {
      autoSlide();
    }
    return () => clearTimeout(slideInterval.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos]);

  const controlButtons = () => {
    if (!showControls || items.length <= 1) return;
    const prevAvailable = pos > 0;
    const nextAvailable = pos + step < items.length;
    // const nextAvailable = pos < items.length - numberOfItems;

    return (
      <>
        {prevAvailable && (
          <ControlButton onClick={handlePrev} direction="prev" height={height}>
            <ChevronLeft size="24px" aria-hidden="true" />
          </ControlButton>
        )}
        {nextAvailable && (
          <ControlButton onClick={handleNext} direction="next" height={height}>
            <ChevronRight size="24px" aria-hidden="true" />
          </ControlButton>
        )}
      </>
    );
  };

  const finalItems = items.map((item, index) => {
    const gridColumnForItem = item.props.gridColumn;
    const opacity = index < pos ? 0 : 1;
    return (
      <ItemWrap
        gridColumn={gridColumnForItem}
        opacity={opacity}
        key={item.uid ?? item.key}
        $type={type}
      >
        {item}
      </ItemWrap>
    );
  });
  const seeAllAnalytics = () => {
    handleSeeAll();
  };

  const pauseCarousel = () => {
    if (!slideAutomatically) return;
    clearTimeout(slideInterval.current);
  };

  const handleSeeAllClick = () => {
    if (href && as) pushRoute(href, as);
  };

  return (
    <Container
      className="carousel-container"
      onMouseOver={pauseCarousel}
      onMouseLeave={autoSlide}
    >
      {label ? (
        <CaraouselInfo className={className}>
          <Column>
            <StyH3>{label}</StyH3>
            {subLabel && <SubLabel>{subLabel}</SubLabel>}
            {extraBenefits}
          </Column>
          {isMobile ? null : <Grow />}
          {as && !isMobile ? (
            <UnderlineLink as={as} href={href} color="var(--color-i-green)">
              <GreenH4
                data-analytics={`subscription-${dataAnalytics}-seeAll`}
                onClick={seeAllAnalytics}
              >
                See all
              </GreenH4>
            </UnderlineLink>
          ) : null}
        </CaraouselInfo>
      ) : null}
      <CarouselWrapper>
        <div>
          <CardContainer
            className="card-carosel-container"
            addPadding={addPadding}
            pos={pos}
          >
            {finalItems}
            {controlButtons()}
          </CardContainer>
        </div>
        {rightShadowDiv && (
          <Right
            $height={rightShadowDivHeight}
            $hideBackground={hideBackground}
          />
        )}
      </CarouselWrapper>
      {type === 'dummy' ? null : (
        <SeeAll label="See All" onClick={handleSeeAllClick} />
      )}
    </Container>
  );
};

CardCarousel.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  subLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showControls: PropTypes.bool,
  className: PropTypes.string,
  step: PropTypes.number,
  addPadding: PropTypes.bool,
  as: PropTypes.string,
  href: PropTypes.string,
  height: PropTypes.string,
  rightShadowDiv: PropTypes.bool,
  rightShadowDivHeight: PropTypes.string,
  dataAnalytics: PropTypes.string,
  type: PropTypes.string.isRequired,
  handleSeeAll: PropTypes.func,
  extraBenefits: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  hideBackground: PropTypes.bool
};

CardCarousel.defaultProps = {
  label: null,
  subLabel: null,
  showControls: true,
  className: '',
  step: 2,
  addPadding: false,
  as: null,
  href: null,
  height: null,
  rightShadowDiv: true,
  rightShadowDivHeight: '320px',
  dataAnalytics: '',
  handleSeeAll: () => null,
  extraBenefits: null,
  hideBackground: false
};
export default CardCarousel;
